import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays, startOfDay, endOfDay, startOfMonth, endOfMonth, subMonths, isSameDay } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useLocationNow } from '@/hooks/useLocationNow';
import { DateRange, DatePreset } from '../types';

// Presets are parameterised by "now" in the LOCATION's timezone so cross-TZ
// admins don't accidentally query a future day or miss the end of "today".
const buildPresetDefs = (now: Date): Array<{ key: string; getValue: () => DateRange }> => [
  { key: 'qa.datePreset.today', getValue: () => ({ from: startOfDay(now), to: endOfDay(now) }) },
  { key: 'qa.datePreset.yesterday', getValue: () => ({ from: startOfDay(subDays(now, 1)), to: endOfDay(subDays(now, 1)) }) },
  { key: 'qa.datePreset.last7days', getValue: () => ({ from: subDays(now, 7), to: now }) },
  { key: 'qa.datePreset.last30days', getValue: () => ({ from: subDays(now, 30), to: now }) },
  // Uses 89 days (not 90) so the resulting duration stays under the 90-day RPC cap
  // after time-of-day and DST drift. User-facing label is still "Last 90 days"
  // (today + 89 prior calendar days).
  { key: 'qa.datePreset.last90days', getValue: () => ({ from: subDays(now, 89), to: now }) },
  { key: 'qa.datePreset.last6months', getValue: () => ({ from: subDays(now, 179), to: now }) },
  { key: 'qa.datePreset.last1year', getValue: () => ({ from: subDays(now, 364), to: now }) },
  { key: 'qa.datePreset.thisMonth', getValue: () => ({ from: startOfMonth(now), to: now }) },
  { key: 'qa.datePreset.lastMonth', getValue: () => ({ from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) }) },
];

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
  /**
   * IANA timezone string of the currently-selected location. When provided,
   * preset calculations ("Today", "Last 7 days", etc.) are anchored to that
   * location's wall clock instead of the browser's.
   */
  timezone?: string;
}

export function DateRangePicker({ value, onChange, className, timezone }: DateRangePickerProps) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  // Draft range tracks the in-progress selection inside the popover.
  // null = no draft yet (calendar shows the committed value).
  const [draft, setDraft] = useState<{ from: Date; to?: Date } | null>(null);

  // "Now" as a wall-clock Date in the location's timezone. Falls back to
  // browser time when no timezone is known (pre-selection / single-TZ orgs).
  const locationNow = useLocationNow(timezone);

  // Build translated presets, anchored to the location clock
  const PRESETS: DatePreset[] = useMemo(() =>
    buildPresetDefs(locationNow).map(def => ({ label: t(def.key), getValue: def.getValue })),
  [t, locationNow]);

  // Determine which preset (if any) matches the current value
  const activePreset = useMemo(() => {
    return PRESETS.find((preset) => {
      const presetValue = preset.getValue();
      return isSameDay(value.from, presetValue.from) && isSameDay(value.to, presetValue.to);
    })?.label ?? null;
  }, [value, PRESETS]);

  const handlePresetClick = (preset: DatePreset) => {
    onChange(preset.getValue());
    setDraft(null);
    setOpen(false);
  };

  // Deterministic two-click selection driven off the raw day click rather than
  // react-day-picker's range computation. The old onSelect path had two bugs:
  //   1. A single day committed the calendar's midnight dates verbatim, so the
  //      range was { from: 00:00, to: 00:00 } — a zero-width window that
  //      returned no data. Picking one day looked broken.
  //   2. rdp's range mode is fiddly about completing a single-day range when
  //      you click the same day twice.
  // Here: first click sets the start; the second click commits — the SAME day
  // twice is a valid single day. Always normalised to start/end-of-day so the
  // selected day(s) are fully covered.
  const handleDayClick = (day: Date) => {
    // No selection in progress → this click is the start.
    if (!draft) {
      setDraft({ from: day });
      return;
    }
    // Second click → complete the range (single day when the same day is
    // re-clicked), ordering the endpoints so a backwards pick still works.
    const start = day < draft.from ? day : draft.from;
    const end = day < draft.from ? draft.from : day;
    onChange({ from: startOfDay(start), to: endOfDay(end) });
    setDraft(null);
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setDraft(null); // Reset draft when closing without completing
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'justify-start text-left font-normal min-w-[280px]',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value?.from ? (
            isSameDay(value.from, value.to) ? (
              // Single-day selection reads as one date, not "X - X".
              <>{format(value.from, 'MMM d, yyyy')}</>
            ) : (
              <>
                {format(value.from, 'MMM d, yyyy')} - {format(value.to, 'MMM d, yyyy')}
              </>
            )
          ) : (
            <span>{t('qa.pickDateRange')}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 flex flex-col sm:flex-row" align="start">
        {/* Presets sidebar - stacks on mobile */}
        <div className="border-b sm:border-b-0 sm:border-r p-2 space-y-1 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible">
          {PRESETS.map((preset) => (
            <Button
              key={preset.label}
              variant={activePreset === preset.label ? 'secondary' : 'ghost'}
              size="sm"
              className={cn('w-full justify-start', activePreset === preset.label && 'font-medium')}
              onClick={() => handlePresetClick(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
        {/* Calendar */}
        <div className="p-2">
          <Calendar
            mode="range"
            selected={draft ?? { from: value.from, to: value.to }}
            onDayClick={handleDayClick}
            numberOfMonths={2}
            defaultMonth={draft?.from ?? value.from}
          />
          <p className="px-2 pb-1 pt-2 text-xs text-muted-foreground">
            {draft
              ? t('qa.datePickerHintEnd', 'Click an end date, or the same day again for a single day')
              : t('qa.datePickerHintStart', 'Click a start date, then an end date')}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
